<script setup>
import { computed, ref } from 'vue'
import { AGENTS_CONSTANTS } from '@/constants/agents.constants'
import i18n from '@/plugins/i18n'

const props = defineProps({
  item: { type: Object, required: true },
  maxPhoneNumberChanges: { type: Number, default: 0 }
})

const emit = defineEmits(['detail', 'start', 'stop', 'show-qr', 'upload-chats', 'show-code', 'refresh', 'reload'])

// Chiavi nuove: finche' non sono in tabella localizations la pagina usa questi testi inglesi.
// Stesso schema gia' adottato in AppSumoRedemptionForm.vue.
const FALLBACKS = {
  'agents.list.startHint': 'Press ▶ to start and connect WhatsApp',
  'agents.list.scanQrHint': 'Scan the QR code to connect WhatsApp',
  'agents.list.connectingHint': 'Connecting… the status updates by itself',
  'agents.action.reload': 'Refresh status'
}

const tr = (key) => (i18n.global.te(key) ? i18n.global.t(key) : (FALLBACKS[key] || key))

const actionsExpanded = ref(false)
const reloading = ref(false)

const onReload = () => {
  reloading.value = true
  emit('reload')
  // Il giro di rete e' breve: lo spinner serve a far capire che il click e' stato preso.
  setTimeout(() => { reloading.value = false }, 1200)
}

const statusColor = computed(() => {
  switch (props.item.status) {
    case 'stopped': return AGENTS_CONSTANTS.STATUS_COLORS.stopped
    case 'active': return AGENTS_CONSTANTS.STATUS_COLORS.active
    case 'action_required': return AGENTS_CONSTANTS.STATUS_COLORS.action_required
    case 'waiting': return AGENTS_CONSTANTS.STATUS_COLORS.waiting
    case 'sleeping': return AGENTS_CONSTANTS.STATUS_COLORS.sleeping
    default: return AGENTS_CONSTANTS.STATUS_COLORS.default
  }
})

const borderClass = computed(() => {
  switch (props.item.status) {
    case 'active': return 'border-active'
    case 'action_required': return 'border-warning'
    default: return null
  }
})

const disableButtons = computed(() => props.item.status === 'pending')

// Un agent appena creato e' "stopped" con un ▶ muto: chi arriva qui la prima volta non sa che
// deve premerlo, ne' che subito dopo va scansionato il QR. Una riga per stato lo dice.
const hint = computed(() => {
  switch (props.item.status) {
    case 'stopped': return { text: tr('agents.list.startHint'), icon: 'mdi-play-circle-outline', cls: 'text-primary' }
    case 'action_required': return { text: tr('agents.list.scanQrHint'), icon: 'mdi-qrcode-scan', cls: 'text-warning' }
    case 'pending':
    case 'waiting': return { text: tr('agents.list.connectingHint'), icon: 'mdi-progress-clock', cls: 'text-grey-7' }
    default: return null
  }
})

// const changesLeft = computed(() => props.maxPhoneNumberChanges - props.item.number_changes_counter)

// const badgeColor = computed(() => {
//   if (changesLeft.value === 0) return 'negative'
//   if (changesLeft.value === 1) return 'warning'
//   return 'white'
// })

// const badgeTextColor = computed(() => {
//   if (changesLeft.value === 1) return 'dark'
//   if (changesLeft.value === 0) return 'white'
//   return 'black'
// })
</script>

<template>
  <q-card
    class="bg-white cursor-pointer hover-border"
    :class="borderClass"
  >
    <q-card-section class="row items-center justify-between no-wrap">
      <div class="text-subtitle1 text-weight-medium text-ellipsis">
        {{ item.name }}
      </div>

      <q-chip
        dense
        square
        :color="statusColor"
        text-color="white"
        class="text-weight-bold q-mr-none"
      >
        {{ $t(`agents.status.${item.status}`) }}
      </q-chip>
    </q-card-section>

    <q-card-section>
      <div class="column q-gutter-y-xs">
        <div class="text-caption text-grey-8">
          {{ $t('agents.list.associatedPhoneNumber') }}:
          <span class="text-weight-medium text-black">
            {{ item.associated_phone_number?.number || '-' }}
          </span>
        </div>

        <!-- <div class="row items-center text-caption text-grey-8">
          <span class="q-mr-xs">{{ $t('agents.list.changesRemaining') }}:</span>
          <q-badge
            :color="badgeColor"
            :text-color="badgeTextColor"
          >
            {{ item.number_changes_counter + ' / ' + maxPhoneNumberChanges }}
          </q-badge>
        </div> -->

        <div
          v-if="hint"
          class="row items-center no-wrap text-caption"
          :class="hint.cls"
        >
          <q-icon
            :name="hint.icon"
            size="18px"
            class="q-mr-xs"
          />
          <span>{{ hint.text }}</span>
        </div>
      </div>
    </q-card-section>

    <q-card-section class="row items-center justify-between">
      <div
        class="text-negative"
      >
        <template v-if="item.is_message_limit_reached">
          {{ $t('agents.maxDailyMessagesReached') }}
        </template>
      </div>

      <div
        v-if="!disableButtons"
        class="row items-center q-gutter-x-sm"
      >
        <q-btn
          v-if="item.status === 'action_required'"
          round
          dense
          unelevated
          color="grey"
          icon="mdi-numeric"
          @click.stop="emit('show-code', item)"
        >
          <q-tooltip>{{ $t('agents.action.code') }}</q-tooltip>
        </q-btn>

        <q-btn
          v-if="item.status === 'action_required'"
          round
          dense
          unelevated
          color="grey"
          icon="mdi-qrcode"
          @click.stop="emit('show-qr', item)"
        >
          <q-tooltip>{{ $t('agents.action.qr') }}</q-tooltip>
        </q-btn>

        <q-btn
          v-if="item.status === 'stopped'"
          round
          dense
          unelevated
          color="positive"
          icon="mdi-play"
          class="play-pulse"
          @click.stop="() => {item.status = 'pending'; emit('start', item)}"
        >
          <q-tooltip>{{ $t('agents.action.start') }}</q-tooltip>
        </q-btn>

        <q-btn
          v-if="item.status === 'waiting'"
          round
          dense
          unelevated
          color="primary"
          icon="mdi-refresh"
          :loading="reloading"
          @click.stop="onReload"
        >
          <q-tooltip>{{ tr('agents.action.reload') }}</q-tooltip>
        </q-btn>

        <q-btn
          v-if="item.status == 'active'"
          round
          dense
          unelevated
          color="grey"
          icon="mdi-sync"
          @click.stop="emit('upload-chats', item)"
        >
          <q-tooltip>{{ $t('agents.action.syncChats') }}</q-tooltip>
        </q-btn>

        <q-btn
          v-if="item.status !== 'stopped'"
          round
          dense
          unelevated
          color="warning"
          icon="mdi-stop"
          @click.stop="() => {item.status = 'pending'; emit('stop', item)}"
        >
          <q-tooltip>{{ $t('agents.action.stop') }}</q-tooltip>
        </q-btn>

        <q-btn
          round
          dense
          flat
          color="grey"
          icon="mdi-pencil"
          @click.stop="emit('detail', item)"
        >
          <q-tooltip>{{ $t('common.edit') }}</q-tooltip>
        </q-btn>
        <q-btn
          v-if="item.status === 'active'"
          round
          dense
          flat
          color="grey"
          :icon="actionsExpanded ? 'mdi-chevron-right' : 'mdi-chevron-left'"
          @click.stop="actionsExpanded = !actionsExpanded"
        >
          <q-tooltip>{{ $t('common.more') }}</q-tooltip>
        </q-btn>

        <q-btn
          v-if="actionsExpanded && item.status === 'active'"
          round
          dense
          unelevated
          color="grey"
          icon="mdi-refresh"
          @click.stop="emit('refresh', item)"
        >
          <q-tooltip>{{ $t('agents.action.refresh') }}</q-tooltip>
        </q-btn>
      </div>

      <!-- Durante la transizione (pending) gli altri comandi restano nascosti come prima:
           al loro posto un Refresh, cosi' non serve piu' ricaricare la pagina per vedere l'esito. -->
      <div
        v-else
        class="row items-center q-gutter-x-sm"
      >
        <q-btn
          round
          dense
          unelevated
          color="primary"
          icon="mdi-refresh"
          :loading="reloading"
          @click.stop="onReload"
        >
          <q-tooltip>{{ tr('agents.action.reload') }}</q-tooltip>
        </q-btn>
      </div>
    </q-card-section>
  </q-card>
</template>

<style scoped>
/* Stesso battito del "+" delle pagine elenco: chi crea il primo agent non capiva che il ▶
   andasse premuto. Si ferma sotto il mouse e rispetta "riduci animazioni". */
.play-pulse {
  animation: play-pulse 2.6s ease-in-out infinite;
}

.play-pulse:hover {
  animation-play-state: paused;
}

@keyframes play-pulse {
  0%, 100% { transform: scale(1); box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2); }
  50%      { transform: scale(1.12); box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3); }
}

@media (prefers-reduced-motion: reduce) {
  .play-pulse { animation: none; }
}
</style>
